import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { completeAppraisal, completeReappraisal } from "../../api/requests";
import { errorMessages } from "../../data/filterTypes";
import CustomException from "../../errors";
import { useRetryRequest } from "../../hooks/useRetryRequest";
import {
  getAppraisalData,
  getHasAllRequiredPhotosUploaded,
  getIsLoading,
  getSourceSystemContent,
  getSourceSystemIsBranchAppraisal,
  getSourceSystemIsSell,
  selectAppraisal
} from "../../store/selectors";
import { appraisalSlice, postTellUsMore } from "../../store/slices/Appraisal";
import { isQuestionsAnswered, isVehicleCommercialVehicle } from "../helpers";
import { useNavigate } from "react-router-dom";
import { useSourceSystem } from "../../hooks/useSourceSystem";
import { setLocalStorageDataForAppraisalReuse } from "../../sessionAndLocalStorage";
import { sendEvent } from "../../helpers/analytics";

export const useInitialOffer = () => {
  const { sourceSystem } = useSourceSystem();
  const {
    showEstimatedOfferPartExchange,
    isPartExchangeCustomer,
    showBranchValuationContent,
    initialOfferContent: {
      cta,
      ctaMicro,
      stepNumber,
      assumptionsContent,
      subheaderTitle
    },
    estimatedOfferCTA,
    estimatedOfferNoValuationCTACopy,
    analyticSearchParam,
    getStepNumber
  } = sourceSystem;
  const { valuation, vehicle, appraisalSessionId, promoApplied } =
    useSelector(getAppraisalData);
  const isCommercialVehicle = isVehicleCommercialVehicle(vehicle);
  const {
    appraisalLoading,
    valuationLoading,
    answersChanged,
    isReuseAppraisal,
    isSavedDealReappraisal
  } = useSelector(selectAppraisal);
  const isSell = useSelector(getSourceSystemIsSell);
  const { quickValuation } = useSelector(getSourceSystemContent);
  const isQuotations = sourceSystem.name === "quotations";
  const allImagesUploaded = useSelector(getHasAllRequiredPhotosUploaded);
  const isBranchAppraisal = useSelector(getSourceSystemIsBranchAppraisal);
  const isLoading = useSelector(getIsLoading);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const happensNextCardRef = useRef(null);
  const tellUsMoreCardRef = useRef(null);
  const [showSpinner, setShowSpinner] = useState(false);

  const { retryRequest, appraisalAndValuationRequest, valuationRequest } =
    useRetryRequest();

  const { questions, questionsAndAnswers } = useSelector(getAppraisalData);
  const allQuestions = questions.map((q) => q.questions).flat();

  const answersComplete = isQuestionsAnswered(
    allQuestions,
    questionsAndAnswers
  );

  const reuseAppraisalPromo =
    isReuseAppraisal && valuation?.promotionalBonus > 0;

  const promoActionNotRequired =
    promoApplied === valuation?.promotionAppliedToValuation ||
    !valuation ||
    reuseAppraisalPromo;
  const showPromoBranding =
    !promoActionNotRequired || promoApplied || reuseAppraisalPromo;

  const showExpired =
    !!valuation?.valuationWithoutMotPenalty &&
    valuation?.valuationWithoutMotPenalty !== valuation?.valuation;

  const getAssumptionsAnalytics = (offerType) => {
    return {
      "data-gtm-track": "interaction",
      "data-gtm-track-label": `These assumptions from ${offerType}`
    };
  };

  const handleTellUsMoreSubmit = async (formData) => {
    const { tellUsMore } = formData;
    const payload = { Information: tellUsMore };
    dispatch(postTellUsMore({ appraisalSessionId, payload })).then(
      async ({ payload }) => {
        dispatch(appraisalSlice.actions.tellUsMoreClearError());
        if (payload.Error) {
          await dispatch(
            appraisalSlice.actions.setTellUsMoreError(
              "An error has occured please try again"
            )
          );
        } else {
          await dispatch(appraisalSlice.actions.setTellUsMoreSubmitted(true));
        }
      }
    );
  };

  async function handleSubmit(currentPath) {
    if (analyticSearchParam) {
      sendEvent("event", "interaction", {
        label: "Initial Valuation"
      });
    }
    if (
      currentPath === "/initial-offer" &&
      isPartExchangeCustomer &&
      allImagesUploaded
    ) {
      dispatch(appraisalSlice.actions.setQuickValuationPartExchange(true));
      return navigate("/photos-intro");
    }

    if (currentPath === "/initial-offer" && isPartExchangeCustomer) {
      dispatch(appraisalSlice.actions.setQuickValuationPartExchange(true));
      return navigate("/photos-intro");
    }

    // Check if promo has applied if required
    dispatch(appraisalSlice.actions.setOnSubmitPromoError(false));
    if (!promoActionNotRequired && !promoApplied) {
      dispatch(appraisalSlice.actions.setOnSubmitPromoError(true));
      return;
    }

    // valuation has been done from branch tablet
    if (isBranchAppraisal) {
      // reappraisal request
      await completeReappraisal(appraisalSessionId)
        .then(({ status, data }) => {
          if (status === 200) {
            dispatch(appraisalSlice.actions.setIsOnline(true));
            navigate("/branch-valuation-confirmation");
          } else {
            throw new CustomException(data.error);
          }
        })
        .catch((error) => {
          setShowSpinner(false);
          if (error.message === errorMessages.NETWORKERROR) {
            retryRequest(handleSubmit);
          } else {
            if (!!error) {
              dispatch({ type: "SET_ERROR", data: error.code });
            }
            navigate("/error");
          }
        });
    } else {
      setShowSpinner(true);
      await completeAppraisal(appraisalSessionId)
        .then(({ status, data }) => {
          if (status === 200) {
            dispatch(appraisalSlice.actions.setIsOnline(true));
            window.location.href = data.result.redirectUri;
          } else {
            throw new CustomException(data.error);
          }
        })
        .catch((error) => {
          setShowSpinner(false);
          if (error.message === errorMessages.NETWORKERROR) {
            retryRequest(handleSubmit);
          } else {
            if (!!error) {
              dispatch({ type: "SET_ERROR", data: error.code });
            }
            navigate("/error");
          }
        });
    }
  }

  setLocalStorageDataForAppraisalReuse(
    valuation,
    vehicle,
    isSavedDealReappraisal
  );

  return {
    isCommercialVehicle,
    isSell,
    stepNumber,
    valuation,
    showExpired,
    promoActionNotRequired,
    ctaMicro,
    cta,
    subheaderTitle,
    assumptionsContent,
    showSpinner,
    handleSubmit,
    appraisalLoading,
    valuationLoading,
    appraisalSessionId,
    promoApplied,
    vehicle,
    isLoading,
    dispatch,
    appraisalAndValuationRequest,
    valuationRequest,
    answersChanged,
    answersComplete,
    happensNextCardRef,
    showEstimatedOfferPartExchange,
    showBranchValuationContent,
    tellUsMoreCardRef,
    handleTellUsMoreSubmit,
    analyticSearchParam,
    isQuotations,
    isPartExchangeCustomer,
    getStepNumber,
    questions,
    questionsAndAnswers,
    quickValuation,
    reuseAppraisalPromo,
    estimatedOfferCTA,
    showPromoBranding,
    estimatedOfferNoValuationCTACopy,
    getAssumptionsAnalytics
  };
};
